import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { AnalysisModelInfo, RaceMatrixData } from '../lib/history-performance';
import { isViewableAnalysisSlot } from '../lib/analysis-models';

export function useAnalysisModels(meetingId: string, enabled = true) {
  return useQuery({
    queryKey: ['meeting-analysis-models', meetingId],
    queryFn: () => apiClient<AnalysisModelInfo[]>(`/meetings/${meetingId}/analysis-models`),
    select: (models: AnalysisModelInfo[]) => models.filter((m) => isViewableAnalysisSlot(m.analysis_type_id)),
    enabled,
  });
}

// The caller (RaceMatrixView) only mounts on the Matrix tab, so this query
// naturally only fires when that tab is active — no separate `enabled` gate needed.
export function useRaceModelMatrix(meetingId: string) {
  return useQuery({
    queryKey: ['meeting-race-model-matrix', meetingId],
    queryFn: () => apiClient<RaceMatrixData>(`/meetings/${meetingId}/race-model-matrix`),
    select: (matrix: RaceMatrixData) => ({ ...matrix, slots: matrix.slots.filter((s) => isViewableAnalysisSlot(s.analysisTypeId)) }),
  });
}

export interface HistoryRow {
  meeting_id: string;
  meeting_date: string;
  day_name: string;
  total_races: number | string;
  top_1_winners: number | string;
  value_winners: number | string | null;
  all_picks_winners: number | string | null;
  top_1_roi: number | string | null;
  value_roi: number | string | null;
  prompt_name: string | null;
  track_condition: string | null;
  track_condition_number: string | null;
  form_weighting: number | null;
  market_weighting: number | null;
  ai_version: string | null;
  ai_model: string | null;
}

// Same mount-gating as useRaceModelMatrix — only fires while the History tab is active.
export function useHistoricalPerformance(meetingId: string) {
  return useQuery({
    queryKey: ['meeting-historical-performance', meetingId],
    queryFn: () => apiClient<HistoryRow[]>(`/meetings/${meetingId}/historical-performance`),
  });
}

function useInvalidateAnalysis(meetingId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['meeting-analysis-models', meetingId] });
    queryClient.invalidateQueries({ queryKey: ['meeting-race-model-matrix', meetingId] });
    queryClient.invalidateQueries({ queryKey: ['meeting-races-status', meetingId] });
    // every swap re-runs the auto review on the server
    queryClient.invalidateQueries({ queryKey: ['meeting-auto-review', meetingId] });
  };
}

interface SwapAnalysisInput {
  raceIds?: string[];
  fromSlot: number;
  toSlot: number;
}

export interface SwapAnalysisResult {
  success: boolean;
  rowsAffected: number;
  fromSlot: number;
  toSlot: number;
  /** T5 ⇄ T1 only: the races whose T1 now holds the auto review's suggestion */
  appliedRaceIds?: string[];
}

export function useSwapAnalysis(meetingId: string) {
  const invalidate = useInvalidateAnalysis(meetingId);
  return useMutation({
    mutationFn: (body: SwapAnalysisInput) =>
      apiClient<SwapAnalysisResult>(
        `/meetings/${meetingId}/swap-analysis`,
        { method: 'POST', body: JSON.stringify(body) }
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteAnalysis(meetingId: string) {
  const invalidate = useInvalidateAnalysis(meetingId);
  return useMutation({
    mutationFn: () =>
      apiClient<{ success: boolean; message: string; deleted_count: number }>(`/meetings/${meetingId}/analysis`, {
        method: 'DELETE',
      }),
    onSuccess: invalidate,
  });
}
